import { FC, useCallback, useRef, useState } from "react";
import { FlatList, Platform, ScrollView, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import { tHandleColumnSortProps, tRowId, tSortConfig, tSuperTableProps } from "./types";
import {
  buildEmptyActiveColumnConfig,
  generateCSV,
  HeaderBig,
  HeaderSmall,
  RenderCard,
  RenderRow,
  sortColumn,
  TableTitleHeader,
} from "./util";
import { defaultRenderCell } from "./renderHelpers";
import { kColors } from "../../kColors";
import { useEventListener } from "usehooks-ts";
import { EmptyMessage } from "..";
import { kStyleConst } from "../../kStyle";
import { useAppDispatch } from "src/redux";

const SuperTable = <T, C = tRowId>({
  data,
  columnNames,
  columnOrder,
  renderCell = defaultRenderCell,
  renderRowFooter,
  rowContainerStyle,
  onPress,
  sortConfig,
  shadeAlternates,
  loading,
  emptyTableMessage,
  columnMinWidthConfig,
  defaultSort,
  canConfig = true,
  canCopyAsCSV = true,
  title,
  reduxKey,
}: tSuperTableProps<T, C>): JSX.Element => {
  const dispatch = useAppDispatch();
  const rDeviceSize = useSelector((state) => state.NavigationReducer.deviceSize);
  const rTableConfigs = useSelector((state) => (reduxKey ? state.TableConfigReducer[reduxKey] : undefined));
  const kDefaultSortConfig: tSortConfig<T> = defaultSort
    ? { columnKey: defaultSort.myColumnKey, ascending: defaultSort.isAscending }
    : null;
  const [sCurrentSortConfig, _setCurrentSortConfig] = useState<tSortConfig<T>>(kDefaultSortConfig);
  const aTableData = sortColumn({
    myData: data,
    myColumnKey: sCurrentSortConfig?.columnKey,
    myIsAscending: sCurrentSortConfig?.ascending,
    renderCell,
  });

  const [sIsConfiguring, _setIsConfiguring] = useState<boolean>(false);
  const _handleToggleIsConfiguring = useCallback(() => _setIsConfiguring(!sIsConfiguring), [sIsConfiguring]);

  const kInitialActiveColumnState: Record<string | number | symbol, boolean> | undefined =
    rTableConfigs && Object.keys(rTableConfigs).length > 0
      ? rTableConfigs
      : buildEmptyActiveColumnConfig({ columnOrder });

  const [sActiveColumns, _setActiveColumns] = useState(kInitialActiveColumnState);

  const _handleCopyAsCSV = useCallback(() => {
    navigator.clipboard
      .writeText(generateCSV({ data, columnOrder, columnNames, renderCell, activeColumns: sActiveColumns }))
      .then(() => {
        dispatch({ type: "RIBBON", message: "Copied to clipboard" });
      })
      .catch(() => {
        dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
      });
  }, [data, columnOrder, columnNames, renderCell, sActiveColumns, dispatch]);

  const _handleColumnSort = useCallback(
    ({ myColumnKey, isAscending }: tHandleColumnSortProps<T>) => {
      if (isAscending !== undefined) {
        _setCurrentSortConfig({
          columnKey: myColumnKey,
          ascending: isAscending,
        });
      } else {
        if (sCurrentSortConfig?.columnKey === myColumnKey) {
          if (sCurrentSortConfig?.ascending) {
            _setCurrentSortConfig({
              columnKey: myColumnKey,
              ascending: false,
            });
          } else {
            _setCurrentSortConfig({
              columnKey: myColumnKey,
              ascending: true,
            });
          }
        } else {
          _setCurrentSortConfig({
            columnKey: myColumnKey,
            ascending: true,
          });
        }
      }
    },
    [sCurrentSortConfig],
  );

  const _handleRenderHeader: FC = useCallback(
    () =>
      rDeviceSize === "lg" || rDeviceSize === "md" ? (
        <HeaderBig
          columnOrder={columnOrder}
          columnNames={columnNames}
          sortConfig={sortConfig}
          _handleColumnSort={_handleColumnSort}
          columnMinWidthConfig={columnMinWidthConfig}
          currentSortConfig={sCurrentSortConfig}
          canConfig={canConfig}
          activeColumns={sActiveColumns}
        />
      ) : null,
    [
      rDeviceSize,
      sCurrentSortConfig,
      columnOrder,
      columnNames,
      _handleColumnSort,
      sortConfig,
      columnMinWidthConfig,
      canConfig,
      sActiveColumns,
    ],
  );

  const _handleRenderItem = useCallback(
    ({ item, index }) => {
      if (rDeviceSize !== "lg" && rDeviceSize !== "md")
        return (
          <RenderCard
            rowData={item}
            rowIndex={index}
            columnOrder={columnOrder}
            columnNames={columnNames}
            loading={loading || false}
            onPress={onPress}
            shadeAlternates={shadeAlternates}
            renderCell={renderCell}
            deviceSize={rDeviceSize}
            renderRowFooter={renderRowFooter}
            rowContainerStyle={rowContainerStyle}
            key={item.meta.key}
            activeColumns={sActiveColumns}
            total={data.length}
          />
        );
      return (
        <RenderRow
          rowData={item}
          rowIndex={index}
          columnOrder={columnOrder}
          columnNames={columnNames}
          loading={loading || false}
          onPress={onPress}
          shadeAlternates={shadeAlternates}
          columnMinWidthConfig={columnMinWidthConfig}
          renderCell={renderCell}
          renderRowFooter={renderRowFooter}
          rowContainerStyle={rowContainerStyle}
          key={item.meta.key}
          activeColumns={sActiveColumns}
          total={data.length}
        />
      );
    },
    [
      rDeviceSize,
      columnOrder,
      columnNames,
      loading,
      onPress,
      shadeAlternates,
      renderCell,
      renderRowFooter,
      rowContainerStyle,
      sActiveColumns,
      columnMinWidthConfig,
      data.length,
    ],
  );

  const _handleEmptyListComponent = useCallback(
    () => (
      <EmptyMessage message={emptyTableMessage || "There is no data to display"} viewStyle={styles.emptyMessageView} />
    ),
    [emptyTableMessage],
  );

  const kMenuRef = useRef<View>(null);
  useEventListener("click", (event) => {
    if (Platform.OS === "web") {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const kPointerEvent = event as PointerEvent;
      kMenuRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
        const isInXRegion = kPointerEvent.pageX > pageX && kPointerEvent.pageX < pageX + width;
        const isInYRegion = kPointerEvent.pageY > pageY && kPointerEvent.pageY < pageY + height;

        if (!(isInXRegion && isInYRegion)) {
          _setIsConfiguring(false);
        }
      });
    }
  });

  if (columnMinWidthConfig) {
    return (
      <View>
        <TableTitleHeader
          title={title}
          canConfig={canConfig}
          canCopyAsCSV={canCopyAsCSV}
          _handleCopyAsCSV={_handleCopyAsCSV}
          _handleToggleIsConfiguring={_handleToggleIsConfiguring}
          isConfiguring={sIsConfiguring}
          menuRef={kMenuRef}
          activeColumns={sActiveColumns}
          columnNames={columnNames}
          columnOrder={columnOrder}
          setActiveColumns={_setActiveColumns}
          reduxKey={reduxKey}
        />
        <ScrollView
          contentContainerStyle={
            rDeviceSize !== "lg" && rDeviceSize !== "md"
              ? [styles.flexOne, { width: "100%" }]
              : [styles.flexOne, { minWidth: Object.keys(sActiveColumns).length * (columnMinWidthConfig ?? 0) }]
          }
          horizontal={rDeviceSize === "lg" || rDeviceSize === "md"}
        >
          <View style={styles.mTableContainer}>
            {rDeviceSize !== "lg" && rDeviceSize !== "md" && (
              <HeaderSmall
                columnOrder={columnOrder}
                columnNames={columnNames}
                sortConfig={sortConfig}
                _handleColumnSort={_handleColumnSort}
                currentSortConfig={sCurrentSortConfig}
              />
            )}
            <FlatList
              style={styles.flexOne}
              ListHeaderComponent={_handleRenderHeader}
              ListEmptyComponent={_handleEmptyListComponent}
              data={aTableData}
              renderItem={_handleRenderItem}
              scrollEnabled={rDeviceSize !== "lg" && rDeviceSize !== "md"}
              showsHorizontalScrollIndicator={true}
              keyExtractor={(item) => item.meta.key.toString()}
            />
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View>
      <TableTitleHeader
        title={title}
        canConfig={canConfig}
        canCopyAsCSV={canCopyAsCSV}
        _handleCopyAsCSV={_handleCopyAsCSV}
        _handleToggleIsConfiguring={_handleToggleIsConfiguring}
        isConfiguring={sIsConfiguring}
        menuRef={kMenuRef}
        activeColumns={sActiveColumns}
        columnNames={columnNames}
        columnOrder={columnOrder}
        setActiveColumns={_setActiveColumns}
        reduxKey={reduxKey}
      />
      <View style={styles.mTableContainer}>
        {rDeviceSize !== "lg" && rDeviceSize !== "md" && (
          <HeaderSmall
            columnOrder={columnOrder}
            columnNames={columnNames}
            sortConfig={sortConfig}
            _handleColumnSort={_handleColumnSort}
            currentSortConfig={sCurrentSortConfig}
          />
        )}
        <FlatList
          style={styles.flexOne}
          ListHeaderComponent={_handleRenderHeader}
          ListEmptyComponent={_handleEmptyListComponent}
          data={aTableData}
          renderItem={_handleRenderItem}
          scrollEnabled={true}
          showsHorizontalScrollIndicator={true}
          keyExtractor={(item) => item.meta.key.toString()}
        />
      </View>
    </View>
  );
};

export default SuperTable;

const styles = StyleSheet.create({
  emptyMessageView: {
    paddingVertical: kStyleConst.kSpacing * 5,
  },
  flexOne: {
    flex: 1,
  },
  mTableContainer: {
    backgroundColor: kColors.white,
    flex: 1,
  },
});
